import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { ChevronLeft, Video, Headphones, MessageSquare, ExternalLink, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const MODE_ICON = { video: Video, audio: Headphones, chat: MessageSquare };

export default function Seance() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ap, setAp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/appointments/${id}`);
        setAp(data.appointment);
      } catch (e) {
        toast.error("Séance introuvable.");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading) return <div className="container-jamm py-20 text-ink-soft" data-testid="seance-loading">Chargement…</div>;
  if (!ap) return null;

  const Icon = MODE_ICON[ap.mode] || Video;

  const copyLink = () => {
    if (!ap.jitsi_url) return;
    navigator.clipboard.writeText(ap.jitsi_url);
    setCopied(true);
    toast.success("Lien copié");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="container-jamm py-8" data-testid="seance-page">
      <Link to="/dashboard" className="inline-flex items-center text-ink-soft hover:text-sage-600 text-sm" data-testid="seance-back">
        <ChevronLeft className="w-4 h-4" /> Retour à mon espace
      </Link>

      <div className="mt-6 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <div className="card-soft">
            <div className="flex items-center gap-3 mb-5">
              <div className="grid place-items-center w-11 h-11 rounded-2xl bg-sage-50 text-sage-700">
                <Icon className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div className="min-w-0">
                <div className="font-display text-xl text-ink leading-tight">Séance avec {ap.accompagnateur_name}</div>
                <div className="text-sm text-ink-mute mt-1">{ap.date} · {ap.time} · {ap.duration_min} min · {ap.mode}</div>
              </div>
            </div>

            {ap.mode === "chat" ? (
              <div className="py-10 text-center text-ink-soft" data-testid="seance-chat-mode">
                <MessageSquare className="w-8 h-8 text-ink-mute mx-auto" strokeWidth={1.5} />
                <p className="mt-4">Cette séance est prévue en chat. La messagerie sera bientôt disponible. En attendant, votre accompagnateur·trice vous contactera.</p>
              </div>
            ) : ap.status === "cancelled" ? (
              <div className="py-10 text-center text-ink-soft">Cette séance a été annulée.</div>
            ) : ap.jitsi_url ? (
              <div className="rounded-2xl bg-gradient-to-br from-sage-50 to-bone border border-sage-100 p-8 text-center">
                <Video className="w-10 h-10 text-sage-600 mx-auto" strokeWidth={1.4} />
                <h3 className="font-display text-2xl text-ink mt-4">Votre salon est prêt</h3>
                <p className="text-ink-soft text-sm mt-2 max-w-md mx-auto">
                  Cliquez sur le bouton ci-dessous : votre séance s'ouvre dans un nouvel onglet, sans limite de durée et sans création de compte.
                </p>
                <a
                  href={ap.jitsi_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-sage-500 hover:bg-sage-600 text-white px-7 py-3.5 mt-6 font-semibold shadow-md transition"
                  data-testid="seance-open-jitsi"
                >
                  <ExternalLink className="w-4 h-4" /> Rejoindre la séance
                </a>
                <div className="mt-5 flex items-center justify-center gap-2 text-xs text-ink-mute">
                  <button
                    type="button"
                    onClick={copyLink}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1.5 hover:border-sage-300 transition"
                    data-testid="seance-copy"
                  >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} Copier le lien de la séance
                  </button>
                </div>
                <p className="mt-4 text-[11px] text-ink-mute">
                  Astuce : ouvrez ce lien quelques minutes avant l'heure prévue pour vérifier votre caméra et votre micro.
                </p>
              </div>
            ) : (
              <div className="py-10 text-center text-ink-soft">Salon vidéo non disponible.</div>
            )}
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="card-soft sticky top-24" data-testid="seance-sidebar">
            <div className="overline">Conseils</div>
            <h3 className="font-display text-2xl text-ink mt-2">Pour une séance sereine</h3>
            <ul className="mt-4 space-y-3 text-[15px] text-ink-soft leading-relaxed">
              <li>Trouvez un endroit calme, sans interruption.</li>
              <li>Casque et écouteurs : meilleur son, plus d'intimité.</li>
              <li>Coupez la caméra à tout moment si vous préférez.</li>
              <li>Mode anonyme : votre nom restera caché à l'accompagnateur·trice.</li>
            </ul>
            <div className="border-t border-border mt-6 pt-4 text-xs text-ink-mute leading-relaxed">
              Sama Écoute n'enregistre aucune séance. Tout reste entre vous et votre accompagnateur·trice.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
